import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import {FeatureGroup} from '../../generic/classes/featuregroup';
import {FilterState} from '../../generic/classes/filter.state';
import {SelectedFilter} from '../../generic/classes/selected.filter';
import {FilterService} from '../../generic/service/filter.service';
import {Feature} from '../../generic/classes/feature';

@Component({
    selector: 'selected-filters',
    styleUrls: [
        './filter.component.scss',
        '../../../assets/style/_overview-bar.scss',
        './selected.filters.component.scss',
    ],
    templateUrl: './selected.filters.component.html',
    styles: [
        `
			.translation {
				display: none;
			}
		`
    ],
    standalone: true
})
export class SelectedFiltersComponentComponent {
	private filterService = inject(FilterService);

	private static RETURN_TO_FILTERED_VIEW = 0;

	selectedFilters: SelectedFilter[];

	@ViewChild('translationPotSizeFrom')
	translationPotSizeFrom: ElementRef;

	@ViewChild('translationPotSizeTo')
	translationPotSizeTo: ElementRef;

	@ViewChild('translationPriceFrom')
	translationPriceFrom: ElementRef;

	@ViewChild('translationPriceTo')
	translationPriceTo: ElementRef;

	@ViewChild('translationSearchTerm')
	translationSearchTerm: ElementRef;

	@ViewChild('translationGrower')
	translationGrower: ElementRef;

	@ViewChild('translationItemNo')
	translationItemNo: ElementRef;

	@ViewChild('translationHeightFrom')
	translationHeightFrom: ElementRef;

	@ViewChild('translationHeightTo')
	translationHeightTo: ElementRef;

	@ViewChild('translationClearFeatureFilter')
	translationClearFeatureFilter: ElementRef;

	deselectFeature(featureId: string) {
		this.filterService.toggleFeature(featureId);
	}

	resetFilters() {
		const filterState = this.filterService.getFilterState();
		this.filterService.clearFilterState(filterState);
		this.filterService.notifyFilterStateChange(filterState);
	}

	/**
	 * Called from the products component, so that the selected filters
	 * can be made active when XHR call for resolving the matched products has completed.
	 *
	 * @param {FeatureGroup[]} featureGroups
	 */
	setSelectedFilters(featureGroups: FeatureGroup[]) {
		if (!featureGroups) {
			return;
		}

		const flatten = list => list.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []);

		const filterService = this.filterService;
		const filterState = filterService.getFilterState();

		let selectedFilters: SelectedFilter[] = [];

		this.addSearchTextFilter(filterService, selectedFilters);

		this.addGrowerFilter(filterService, selectedFilters);

		this.addItemNoFilter(filterService, selectedFilters);

		this.addPotSizeFilters(filterState, filterService, selectedFilters);

		this.addPriceFilters(filterState, filterService, selectedFilters);

		this.addHeightFilters(filterState, filterService, selectedFilters);

		this.addClearFeatureFilter(filterService, selectedFilters);

		selectedFilters = selectedFilters.concat(
			flatten(featureGroups.map(group => group.features))
				.filter(feature => filterService.isFeatureSelected(feature.id))
				.map(feature => this.createSelectedFilterObject(feature))
		);

		this.selectedFilters = selectedFilters;
	}

	private getTranslation(translationElementRef: ElementRef): string {
		return translationElementRef.nativeElement.textContent;
	}

	private addPotSizeFilters(
		filterState: FilterState,
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const potSizeFrom = filterState.potSizeFrom;
		const potSizeTo = filterState.potSizeTo;

		if (potSizeFrom) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-potsize-from',
					this.getTranslation(this.translationPotSizeFrom) +
					' ' +
					potSizeFrom,
					() => {
						filterService.setPotSize(null, potSizeTo);
					}
				)
			);
		}

		if (potSizeTo) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-potsize-to',
					this.getTranslation(this.translationPotSizeTo) +
					' ' +
					potSizeTo,
					() => {
						filterService.setPotSize(potSizeFrom, null);
					}
				)
			);
		}
	}

	private addPriceFilters(
		filterState: FilterState,
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const priceFrom = filterState.priceFrom;
		const priceTo = filterState.priceTo;

		if (priceFrom) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-price-from',
					this.getTranslation(this.translationPriceFrom) +
					' ' +
					priceFrom,
					() => {
						filterService.setPrice(null, priceTo);
					}
				)
			);
		}

		if (priceTo) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-price-to',
					this.getTranslation(this.translationPriceTo) +
					' ' +
					priceTo,
					() => {
						filterService.setPrice(priceFrom, null);
					}
				)
			);
		}
	}

	private addHeightFilters(
		filterState: FilterState,
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const heightFrom = filterState.heightFrom;
		const heightTo = filterState.heightTo;

		if (heightFrom) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-height-from',
					this.getTranslation(this.translationHeightFrom) +
					' ' +
					heightFrom,
					() => {
						filterService.setHeight(null, heightTo);
					}
				)
			);
		}

		if (heightTo) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-height-to',
					this.getTranslation(this.translationHeightTo) +
					' ' +
					heightTo,
					() => {
						filterService.setHeight(heightFrom, null);
					}
				)
			);
		}
	}

	private addSearchTextFilter(
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const searchText = filterService.getFilterState().searchText;

		if (searchText) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-searchterm',
					this.getTranslation(this.translationSearchTerm) +
					' ' +
					searchText,
					() => {
						filterService.setSearchText(null);
					}
				)
			);
		}
	}

	/**
	 * Set the state of the clearFeatureFilter. This allows a user who has a prefiltered assortment to still search through
	 * the whole inventory. In the UI we will show the 'Extended search' selected filter when we are in state 1. Every other
	 * state needs to remove this 'selected filter' so we check for that specific state.
	 *
	 * 0 = nothing has happened and just show the correct assortment, build the tables in the database
	 * 1 = The clearFeatureFilter has been applied and the user is searching through the whole inventory
	 * 3 = The clearFeatureFilter has been cleared from the filters and get the default state from the async tables
	 *
	 * @param filterService
	 * @param selectedFilters
	 */
	private addClearFeatureFilter(
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		if (filterService.getFilterState().clearFeatureFilter === 1) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-clearfeaturefilter',
					this.getTranslation(this.translationClearFeatureFilter),
					() => {
						filterService.setClearFeatureFilter(
							SelectedFiltersComponentComponent.RETURN_TO_FILTERED_VIEW,
						);
					}
				)
			);
		}
	}

	private addGrowerFilter(
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const grower = filterService.getFilterState().grower;

		if (grower) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-grower',
					this.getTranslation(this.translationGrower),
					() => {
						filterService.setGrower(null);
					}
				)
			);
		}
	}

	private addItemNoFilter(
		filterService: FilterService,
		selectedFilters: SelectedFilter[],
	) {
		const itemNo = filterService.getFilterState().itemNo;

		if (itemNo) {
			selectedFilters.push(
				new SelectedFilter(
					'filter-itemno',
					this.getTranslation(this.translationItemNo) + ' ' + itemNo,
					() => {
						filterService.setItemNo(null);
					}
				)
			);
		}
	}

	private createSelectedFilterObject(feature: Feature): SelectedFilter {
		return new SelectedFilter(feature.id, feature.description, filter => {
			this.deselectFeature(filter.id);
		});
	}
}
